/**
 * Voice Call Global Tool
 *
 * Initiates an outbound Twilio voice call to the user's phone number.
 * Triggered when a user asks to be called (e.g., "Can you call me at 619-830-1817?").
 *
 * The actual Twilio API call is made server-side via the `initiateVoiceCall`
 * Cloud Function to keep Twilio credentials out of the browser.
 */

import { getAuth } from 'firebase/auth';
import type {
  GlobalToolDefinition,
  StepContext,
  StepResult,
  ToolTheme,
} from '../types';
import { isAgenticWorkflowsAuthorized } from '../../agenticWorkflows';

// ============================================================================
// Tool State
// ============================================================================

export interface VoiceCallState {
  stage: 'idle' | 'initiating' | 'call_placed' | 'failed';
  phoneNumber: string | null;
  callSid: string | null;
  errorMessage: string | null;
}

// ============================================================================
// Theme
// ============================================================================

const VOICE_CALL_THEME: ToolTheme = {
  accent: '#059669',     // Emerald 600
  accentBg: '#d1fae5',   // Emerald 100
  border: '#10b981',     // Emerald 500
  subtitle: '#065f46',   // Emerald 900
  muted: '#6ee7b7',      // Emerald 300
};

// ============================================================================
// Helper: normalize phone number input from natural language
// ============================================================================

/**
 * Extract and normalize a US phone number from a user message or input string.
 * Returns the number as digits only (without +1), or null if not parseable.
 *
 * Examples:
 *   "6188301817"        → "6188301817"
 *   "(619) 830-1817"    → "6198301817"
 *   "619-830-1817"      → "6198301817"
 *   "+1 619 830 1817"   → "6198301817"
 */
export function extractPhoneNumber(input: string): string | null {
  // Remove all whitespace, parentheses, dashes, dots
  const digits = input.replace(/[\s\-()\.]/g, '').replace(/^\+1/, '');
  // Extract only digit sequences
  const match = digits.match(/\d{10,11}/);
  if (!match) return null;
  const num = match[0];
  // Handle 11-digit US numbers starting with 1
  if (num.length === 11 && num.startsWith('1')) return num.slice(1);
  if (num.length === 10) return num;
  return null;
}

// ============================================================================
// Cloud Function URL
// ============================================================================

const INITIATE_CALL_URL = 'https://us-central1-tensile-tenure-453409-k3.cloudfunctions.net/initiateVoiceCall';

// ============================================================================
// Tool Definition
// ============================================================================

export const voiceCallTool: GlobalToolDefinition<VoiceCallState> = {
  id: 'voice-call',
  name: 'Voice Call',
  description: 'Initiate an outbound voice call to the user via Twilio. Use when a user provides a phone number and asks to be called.',

  // No text-based trigger — this is always orchestrator-routed
  triggerPattern: () => ({ matches: false }),

  buttonIds: {},

  createInitialState: (): VoiceCallState => ({
    stage: 'idle',
    phoneNumber: null,
    callSid: null,
    errorMessage: null,
  }),

  steps: [
    // ========================================================================
    // Step 1: Resolve the phone number from orchestrator inputs or conversation
    // ========================================================================
    {
      id: 'resolve-number',
      name: 'Resolve Phone Number',
      activeDisplayName: 'checking phone number',

      execute: async (
        ctx: StepContext<VoiceCallState>,
        rawInput?: unknown
      ): Promise<StepResult> => {
        const input = rawInput as {
          phoneNumber?: string;
          number?: string;
          to?: string;
        } | undefined;

        // Try orchestrator-provided inputs first
        const rawNumber =
          input?.phoneNumber ||
          input?.number ||
          input?.to ||
          '';

        let phoneNumber = extractPhoneNumber(rawNumber);

        // Fallback: scan recent conversation for a phone number
        if (!phoneNumber) {
          const history = ctx.sharedContext.messageHistory || [];
          // Scan last 5 messages from user
          const recent = history
            .filter((m: { isUser?: boolean; text?: string }) => m.isUser)
            .slice(-5)
            .map((m: { isUser?: boolean; text?: string }) => m.text || '')
            .join(' ');
          phoneNumber = extractPhoneNumber(recent);
        }

        // Also try trigger message itself
        if (!phoneNumber) {
          phoneNumber = extractPhoneNumber(ctx.triggerMessage.text || '');
        }

        if (!phoneNumber) {
          // Ask the user for their phone number
          ctx.messaging.showSpecialMessage({
            header: {
              title: 'what\'s your phone number?',
              icon: '📞',
            },
            sections: [
              {
                type: 'text',
                content: 'to call you, i need your 10-digit phone number. please reply with it and i\'ll call you right away.',
              },
            ],
            theme: 'green',
          });

          // Mark awaiting input so orchestrator doesn't block send
          ctx.setState({ stage: 'idle', phoneNumber: null, callSid: null, errorMessage: null });

          return {
            status: 'awaiting-input',
            nextStep: 'initiate-call',
          };
        }

        ctx.setState({ stage: 'idle', phoneNumber });

        return {
          status: 'completed',
          carryForward: { phoneNumber },
        };
      },
    },

    // ========================================================================
    // Step 2: Initiate the call via Cloud Function
    // ========================================================================
    {
      id: 'initiate-call',
      name: 'Initiate Call',
      activeDisplayName: 'calling you now…',

      execute: async (
        ctx: StepContext<VoiceCallState>,
        rawInput?: unknown
      ): Promise<StepResult> => {
        // rawInput may be carry-forward from step 1, or user reply with their number
        const input = rawInput as { phoneNumber?: string } | undefined;

        let phoneNumber = input?.phoneNumber || ctx.state.phoneNumber;

        // If this step was resumed from awaiting-input, check userInput
        if (!phoneNumber && ctx.userInput) {
          phoneNumber = extractPhoneNumber(ctx.userInput || '');
        }

        if (!phoneNumber) {
          ctx.messaging.showSpecialMessage({
            header: { title: 'couldn\'t find a phone number', icon: '⚠️' },
            sections: [
              {
                type: 'text',
                content: 'please reply with your 10-digit US phone number (e.g. 619-830-1817) and i\'ll call you right away.',
              },
            ],
            theme: 'amber',
          });
          return { status: 'awaiting-input', nextStep: 'initiate-call' };
        }

        ctx.setState({ stage: 'initiating', phoneNumber });

        const stream = ctx.messaging.showPending('calling you now…');

        try {
          // Build context string so voice server knows why we're calling
          const callerContext = 'Legal AI assistant calling from prest0.ai chat. The user requested a call from the chat widget.';

          // The endpoint requires a verified Firebase identity (it resolves the
          // caller's workspace and RBAC server-side) — never a body-supplied uid.
          const user = getAuth().currentUser;
          if (!user) {
            throw new Error('Please sign in to place a call.');
          }
          const idToken = await user.getIdToken();

          const response = await fetch(INITIATE_CALL_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ phoneNumber, callerContext }),
          });

          const data = await response.json() as {
            success: boolean;
            callSid?: string;
            status?: string;
            error?: string;
          };

          if (!data.success) {
            throw new Error(data.error || 'Call initiation failed');
          }

          const formattedNumber = phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');

          ctx.setState({
            stage: 'call_placed',
            phoneNumber,
            callSid: data.callSid || null,
            errorMessage: null,
          });

          stream.finalize({
            payload: {
              kind: 'voice_call.complete',
              toolId: 'voice-call',
              data: {
                phoneNumber,
                formattedNumber,
                callSid: data.callSid || null,
                outcome: 'placed',
              },
              summary: `voice call placed to ${formattedNumber}; ring should arrive shortly`,
            },
            sourceTag: 'voice_call_placed',
          });

          return {
            status: 'completed',
            output: { callSid: data.callSid, phoneNumber },
          };
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Unknown error';

          ctx.setState({
            stage: 'failed',
            errorMessage,
          });

          stream.finalize({
            payload: {
              kind: 'voice_call.error',
              toolId: 'voice-call',
              severity: 'error',
              data: { errorMessage, phase: 'place_call' },
              summary: `voice call failed: ${errorMessage}`,
            },
            sourceTag: 'voice_call_failed',
          });

          return {
            status: 'failed',
            error: new Error(errorMessage),
            userFacingErrorShown: true,
          };
        }
      },

      cleanup: async (ctx: StepContext<VoiceCallState>) => {
        ctx.setState({ stage: 'idle', phoneNumber: null, callSid: null, errorMessage: null });
      },
    },
  ],

  // Voice calling is available on all pages (no auth requirement — it's the user's phone)
  isAuthorized: isAgenticWorkflowsAuthorized,

  theme: VOICE_CALL_THEME,

  canTriggerFromButton: false,
};

export default voiceCallTool;
